import {
  useState,
  useEffect,
  createContext,
  useContext,
  ReactNode,
} from 'react';
import firebase from 'firebase';
import initHelpHero from 'helphero';
import { format } from 'date-fns';
import { auth, facebookAuthProvider, firestore } from 'firebaseSetup';
import { useBrowserStorage } from 'hooks/useBrowserStorage';

const hlp = initHelpHero(process.env.REACT_APP_HELPHERO_KEY || '');

type ProviderProps = AuthProviderProps & {
  user: firebase.User | undefined;
};

const AuthContext = createContext<Partial<ProviderProps>>({});

const day = format(new Date(), 'MM-dd-yyyy');

export const useAuthContext = (): Partial<ProviderProps> =>
  useContext(AuthContext);

export const AuthProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const [localUser, setLocalUser, removeLocalUser] = useBrowserStorage(
    'user',
    undefined,
  );
  const [localUserData, setLocalUserData, removeLocalUserData] =
    useBrowserStorage('userData', undefined);
  const [localUsers, setLocalUsers, removeLocalUsers] = useBrowserStorage(
    'users',
    [],
  );
  const [jobViewDetails, setJobViewDetails, removeJobViewDetails] =
    useBrowserStorage('jobViewDetails', {
      [day]: {
        limit: 5,
        views: 0,
        showJobsModal: true,
      },
    });
  const [unlockJobsDetails, setUnlockJobsDetails, removeUnlockJobsDetails] =
    useBrowserStorage('unlockJobsDetails', {
      [day]: {
        shared: false,
        ratedInterview: false,
        reviewedAgency: false,
      },
    });
  const [firstVisit, setFirstVisit, removeFirstVisit] = useBrowserStorage(
    'firstVisit',
    true,
  );
  const [limit, setLimit] = useState(5);
  const [views, setViews] = useState(0);
  const [sharedLevo, setSharedLevo] = useState(
    unlockJobsDetails?.[day]?.shared || false,
  );
  const [ratedInterview, setRatedInterview] = useState(
    unlockJobsDetails?.[day]?.ratedInterview || false,
  );
  const [reviewedAgency, setReviewedAgency] = useState(
    unlockJobsDetails?.[day]?.reviewedAgency || false,
  );
  const [showJobsModal, setShowJobsModal] = useState(true);
  const [user, setUser] = useState(localUser);
  const [users, setUsers] = useState(localUsers);
  const [userData, setUserData] = useState(localUserData);
  const [emailHasBeenSent, setEmailHasBeenSent] = useState(false);
  const [userSignedOut, setUserSignedOut] = useState(false);
  const [signingUp, setSigningUp] = useState(false);
  const [facebookSigningUp, setFacebookSigningUp] = useState(true);

  const persistUser = (user: firebase.User | null | undefined) => {
    setLocalUser(user);
    setUser(user);
  };

  const persistUserData = (data: UserData) => {
    setLocalUserData(data);
    setUserData(data);
  };

  const persistUsersData = (data: UserData[]) => {
    setLocalUsers(data);
    setUsers(data);
  };

  useEffect(() => {
    auth
      .getRedirectResult()
      .then(async (result) => {
        const { additionalUserInfo, user } = result;
        if (user && additionalUserInfo?.isNewUser) {
          setSigningUp(true);
          const profile = additionalUserInfo?.profile as FacebookProfileType;
          const userData: UserData = {
            id: user.uid,
            facebookProfileId: profile.id,
            email: profile.email,
            firstname: profile.first_name,
            lastname: profile.last_name,
            photoURL: profile.picture.data.url,
          };
          await saveUserData(userData);
          getUserData(userData.id || '');
        }
      })
      .catch((error) => {
        console.log(error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return auth.onAuthStateChanged(async (user) => {
      if (user?.uid) {
        hlp.identify(user.uid);
        await getUserData(user.uid);
      }
      setFacebookSigningUp(false);
      persistUser(user);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleSignup = async (email: string, password: string) => {
    try {
      setSigningUp(true);
      await auth.createUserWithEmailAndPassword(email, password);
    } catch (error) {
      throw error;
    }
  };

  const handleSignin = async (email: string, password: string) => {
    try {
      await auth.signInWithEmailAndPassword(email, password);
    } catch (error) {
      throw error;
    }
  };

  const handleFacebookSignIn = async () => {
    try {
      // facebookAuthProvider.addScope('user_birthday');
      // facebookAuthProvider.addScope('user_gender');
      // facebookAuthProvider.addScope('user_hometown');
      // facebookAuthProvider.addScope('user_location');
      // facebookAuthProvider.addScope('public_profile');
      // facebookAuthProvider.addScope('email');
      await auth.signInWithRedirect(facebookAuthProvider);
    } catch (error) {
      throw error;
    }
  };

  const handleSignout = async () => {
    try {
      setUserSignedOut(true);
      await auth.signOut();
      removeLocalUser();
      removeLocalUsers();
      removeLocalUserData();
      removeJobViewDetails();
      removeFirstVisit();
      removeUnlockJobsDetails();
    } catch (e) {
      setUserSignedOut(false);
      console.error('Error signing out', e);
    }
  };

  const handlePasswordReset = async (email: string) => {
    try {
      await auth.sendPasswordResetEmail(email);
      setEmailHasBeenSent(true);
      setTimeout(() => {
        setEmailHasBeenSent(false);
      }, 3000);
    } catch (error) {
      throw error;
    }
  };

  const getUserData = async (uid: string) => {
    const docRef = firestore.collection('users').doc(uid);
    const doc = await docRef.get();
    const data = doc.data();
    if (data) {
      persistUserData(data);
      updateJobViewDetails({});
      // setFirstVisit(data.firstVisit);
    }
  };

  const saveUserData = async (data: UserData) => {
    try {
      const { id, ...rest } = data;
      await firestore
        .collection('users')
        .doc(id)
        .set({
          ...rest,
        });
    } catch (error) {
      throw error;
    }
  };

  const reauthenticate = async (currentPassword: string) => {
    const currentUser = await auth.currentUser;
    const cred = await firebase.auth.EmailAuthProvider.credential(
      user.email,
      currentPassword,
    );
    await currentUser?.reauthenticateWithCredential(cred);
  };

  const changePassword = async (
    currentPassword: string,
    newPassword: string,
  ) => {
    try {
      await reauthenticate(currentPassword);
      const currentUser = await auth.currentUser;
      try {
        await currentUser?.updatePassword(newPassword);
      } catch (e) {
        throw e;
      }
    } catch (e) {
      throw e;
    }
  };

  const changeEmail = async (currentPassword: string, newEmail: string) => {
    try {
      await reauthenticate(currentPassword);
      const currentUser = await auth.currentUser;
      try {
        await currentUser?.updateEmail(newEmail);
      } catch (e) {
        throw e;
      }
    } catch (e) {
      throw e;
    }
  };

  const updateJobViewDetails = async ({
    updateViews,
    showModal,
    newLimit,
  }: JobViewUpdateDetails) => {
    const { limit = 5 } = userData || {};
    const currentDate = format(new Date(), 'MM-dd-yyyy');
    const value = (jobViewDetails || {})[currentDate];
    let newValue;
    if (value) {
      const { limit: oldLimmit, views, showJobsModal } = value;
      const updatedLimit = newLimit ? newLimit : oldLimmit;
      const updatedViews = updateViews ? views + 1 : views;
      const updatedShow = showModal !== undefined ? showModal : showJobsModal;
      newValue = {
        limit: updatedLimit,
        views: updatedViews,
        showJobsModal: updatedShow,
      };
    } else {
      const views = updateViews ? 1 : 0;
      const showJobsModal = showModal !== undefined ? showModal : true;
      newValue = { limit, views, showJobsModal };
    }
    setJobViewDetails({
      [currentDate]: newValue,
    });
    setLimit(newValue.limit);
    setViews(newValue.views);
    setShowJobsModal(newValue.showJobsModal);
  };

  const updateUnlockJobDetails = async (details: UnlockJobUpdateDetails) => {
    const currentDate = format(new Date(), 'MM-dd-yyyy');
    const value = (unlockJobsDetails || {})[currentDate];
    let newValue;
    if (value) {
      newValue = { ...value, ...details };
    } else {
      newValue = {
        shared: false,
        ratedInterview: false,
        reviewedAgency: false,
        ...details,
      };
    }
    setUnlockJobsDetails({
      [currentDate]: newValue,
    });
    setSharedLevo(newValue.shared);
    setRatedInterview(newValue.ratedInterview);
    setReviewedAgency(newValue.reviewedAgency);
  };

  const getUsers = async () => {
    const collectionRef = firestore.collection('users');
    try {
      await collectionRef.get().then((querySnapshot) => {
        const data = [] as UserData[];
        querySnapshot.forEach((doc) => {
          const id = doc.id;
          const userData = doc.data();
          data.push({ id, ...userData });
        });
        persistUsersData(data);
      });
    } catch (e) {
      throw e;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userData,
        signingUp,
        setSigningUp,
        handleSignup,
        handleSignin,
        handleFacebookSignIn,
        handleSignout,
        handlePasswordReset,
        changePassword,
        changeEmail,
        getUserData,
        emailHasBeenSent,
        limit,
        views,
        showJobsModal,
        updateJobViewDetails,
        firstVisit,
        facebookSigningUp,
        setFirstVisit,
        updateUnlockJobDetails,
        sharedLevo,
        ratedInterview,
        reviewedAgency,
        users,
        getUsers,
        userSignedOut,
        setUserSignedOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
